import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Canvas } from "@react-three/fiber";
import { a, useSpring } from "@react-spring/three";
import { Box } from "@mui/material";
import CustomCamera from "./Components/CustomCamera.js";
import ControlPanel from "./Interface/ControlPanel.js";
import {
  ParticleSource,
  Rail,
  Slits,
  Screen,
  Scale,
} from "./Components/OpticElements.js";
import {
  IntensityBarChart,
  IntensityCurve,
  InterferencePattern,
  computeIntensity,
  computeSingleIntensity,
  computeClassicIntensity
} from "./Components/Messurements.js";
import * as math from "mathjs";
import "./App.css"; // CSS-Datei für das Styling

function App() {
  const [w, setW] = useState(math.unit(1, "nm"));
  const [d, setD] = useState(math.unit(1, "nm"));
  const [percentageD, setPercentageD] = useState(0);
  const [percentageW, setPercentageW] = useState(0);
  const [scale, setScale] = useState(1);
  const [classic, setClassic] = useState(false);
  const [lambda, setLambda] = useState(math.unit(1, "nm"));
  const [distance] = useState(math.unit(3, "m"));
  const [maximumReached, setMaximumReached] = useState(false);
  const [particles, setParticles] = useState([]);
  const [intensityMap, setIntensityMap] = useState({});
  const [showBarChart, setShowBarChart] = useState(false);
  const [showCurve, setShowCurve] = useState(false);
  const [isFiring, setIsFiring] = useState(false); // Zustand für den Timer
  const [cameraPosition, setCameraPosition] = useState([5, 2.5, 5]);
  const [cameraLookAt, setCameraLookAt] = useState([0, 0, -0.5]);
  const [cameraFov, setCameraFov] = useState(45);
  const [cameraMoving, setCameraMoving] = useState(true);
  const [showSlits, setShowSlits] = useState(true);
  const [binSize] = useState(0.005);
  const [isDoubleSlit, setIsDoubleSlit] = useState(2);

  const particlesRef = useRef(particles); // Ref für Partikel

  const cameraRef = useRef();
  const containerRef = useRef();
  const rendererRef = useRef();

  particlesRef.current = particles; // Aktuelle Partikel referenzieren

  const setCamera = (position, lookAt, fovCamera, showSlits, cameraMoving) => {
    setCameraPosition(position);
    setCameraLookAt(lookAt);
    setCameraFov(fovCamera);
    setShowSlits(showSlits);
    setCameraMoving(cameraMoving);
  };

  const slitsSpring = useSpring({
    opacity: showSlits ? 1 : 0,
    config: { mass: 1, tension: 100, friction: 20 },
  });

  const AnimatedSlits = a(Slits);

  const initializeIntensityMap = () => {
    let newIntensityMap = {};

    // Konstante für die Umrechnung von Einheiten auf physikalische Größen
    const screenWidthInMeters = 2; // 200 cm = 2 Meter
    const screenUnits = 4; // 4 Einheiten im Koordinatensystem der Software
    const scaleFactor = screenWidthInMeters / screenUnits; // Skalierungsfaktor von Einheiten zu Metern

    for (let x = -2; x <= 2; x += binSize) {
      const bin = x.toFixed(2);

      // Skalieren der x-Position auf Meter, um sie in computeIntensity zu verwenden
      const xInMeters = x * scaleFactor;
      let intens = [];
      if (classic) {
        intens = computeClassicIntensity(
          math.unit(xInMeters, "m"),
          d,
          w,
          scale,
          distance,
          isDoubleSlit
        );
      } else {
        if (isDoubleSlit === 2) {
          intens = computeIntensity(
            math.unit(xInMeters, "m"),
            lambda,
            d,
            w,
            scale,
            distance
          );
        } else {
          intens = computeSingleIntensity(
            math.unit(xInMeters, "m"),
            lambda,
            d,
            w,
            scale,
            distance,
            isDoubleSlit
          );
        }
      }
      newIntensityMap[bin] = {
        count: 0,
        intensity: intens,
      };
    }
    return newIntensityMap;
  };

  useEffect(() => {
    setIsFiring(false);
    handleClearParticles();
  }, [d, w, lambda, distance, scale, isDoubleSlit]);

  useEffect(() => {
    let timer;
    if (isFiring) {
      timer = setInterval(() => {
        fireParticles(100);
      }, 1); // 1 Millisekunde Abstand
    } else if (timer) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [isFiring]);

  const handleFire = (count) => {
    fireParticles(count);
  };

  const handleFastFire = () => {
    if (particlesRef.current.length >= MAX_PARTICLES) {
      setIsFiring(false);
      setMaximumReached(true);
    }
    setIsFiring(!isFiring);
  };

  const MAX_PARTICLES = 100000;

  const weightedRandomBin = (bins, newIntensityMap) => {
    let selectedBin;
    while (true) {
      const randomBin = bins[Math.floor(Math.random() * bins.length)];
      const randomProbability = Math.random();
      const intensity = newIntensityMap[randomBin].intensity;
      if (randomProbability < intensity && randomProbability > 0.01) {
        selectedBin = randomBin;
        break;
      }
    }
    return selectedBin;
  };

  const adjustParticles = (newParticles, newIntensityMap) => {
    let adjustedParticles = [];

    // Konstante für die Umrechnung von Einheiten auf physikalische Größen
    const screenWidthInMeters = 2; // 200 cm = 2 Meter
    const screenUnits = 2; // 4 Einheiten im Koordinatensystem der Software
    const scaleFactor = screenWidthInMeters / screenUnits; // Skalierungsfaktor von Einheiten zu Metern

    for (let i = 0; i < newParticles.length; i++) {
      // Berechne Abweichungen und wähle die besten Segmente aus
      let bins = Object.keys(newIntensityMap);
      bins = bins.filter((bin) => newIntensityMap[bin] !== undefined); // Entferne undefined keys

      const bin = weightedRandomBin(bins, newIntensityMap);

      let intens = [];
      if (isDoubleSlit === 2) {
        intens = computeIntensity(
          math.unit(parseFloat(bin) * scaleFactor, "m"),
          lambda,
          d,
          w,
          scale,
          distance
        );
      } else {
        intens = computeSingleIntensity(
          math.unit(parseFloat(bin) * scaleFactor, "m"),
          lambda,
          d,
          w,
          scale,
          distance,
          isDoubleSlit
        );
      }

      if (!newIntensityMap[bin]) {
        newIntensityMap[bin] = {
          count: 0,
          intensity: intens, // Umrechnung in Meter
        };
      }

      const x =
        parseFloat(bin) * scaleFactor +
        (Math.random() - 0.5) * binSize * scaleFactor; // x in Meter
      const y = (Math.random() - 0.5) * 3.75 + 0.125; // y bleibt in den Software-Einheiten, falls es nicht in Meter sein soll
      const z = -distance.toNumber("m"); // z bleibt in Meter
      adjustedParticles.push({ x, y, z });
      newIntensityMap[bin].count = (newIntensityMap[bin].count || 0) + 1;
    }

    setParticles((prevParticles) => [...prevParticles, ...adjustedParticles]);
    setIntensityMap(newIntensityMap);
  };

  const fireParticles = (count) => {
    let currentParticlesCount = particlesRef.current.length;

    if (currentParticlesCount + count > MAX_PARTICLES) {
      count = MAX_PARTICLES - currentParticlesCount;
      setIsFiring(false);
      setMaximumReached(true);
    }

    let newParticles = [];
    let newIntensityMap = { ...intensityMap };

    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 4;
      const y = (Math.random() - 0.5) * 4;
      const z = -distance.toNumber("m");
      const bin = (Math.floor((x + 2) / binSize) * binSize - 2).toFixed(2);

      let intens = [];
      if (isDoubleSlit === 2) {
        intens = computeIntensity(
          math.unit(parseFloat(bin), "mm"),
          lambda,
          d,
          w,
          scale,
          distance
        );
      } else {
        intens = computeSingleIntensity(
          math.unit(parseFloat(bin), "mm"),
          lambda,
          d,
          w,
          scale,
          distance,
          isDoubleSlit
        );
      }

      if (!newIntensityMap[bin]) {
        newIntensityMap[bin] = {
          count: 0,
          intensity: intens,
        };
      }

      newIntensityMap[bin].count += 1;
      newParticles.push({ x, y, z });
      currentParticlesCount += 1;

      if (currentParticlesCount >= MAX_PARTICLES) {
        setMaximumReached(true);
        break;
      }
    }

    adjustParticles(newParticles, newIntensityMap);
  };

  const handleClearParticles = () => {
    setParticles([]);
    setIntensityMap(initializeIntensityMap());
    setIsFiring(false);
    setMaximumReached(false);
  };

  const setCameraPosition1 = () =>
    setCamera([5, 2.5, 5], [0, 0, -0.5], 45, true, true);
  const setCameraPosition2 = () =>
    setCamera([0, 0, 0.1], [0, 0, -3], 70, false, false);

  const handleResize = useCallback(() => {
    if (containerRef.current && cameraRef.current) {
      const aspect =
        containerRef.current.clientWidth / containerRef.current.clientHeight;
      cameraRef.current.aspect = aspect;
      cameraRef.current.updateProjectionMatrix();
    }
  }, []);

  useEffect(() => {
    const handleResizeWrapper = () => {
      handleResize();
    };

    window.addEventListener("resize", handleResizeWrapper);
    handleResizeWrapper(); // Initial call to set the size correctly

    return () => {
      window.removeEventListener("resize", handleResizeWrapper);
    };
  }, [handleResize]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
        width: "100%",
        flexGrow: 1,
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexGrow: 1,
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
        }}
        ref={containerRef}
      >
        <Box className="canvas-container">
          <Canvas className="canvas-element" ref={rendererRef}>
            <CustomCamera
              ref={cameraRef}
              position={cameraPosition}
              lookAt={cameraLookAt}
              fov={cameraFov}
              enableControls={cameraMoving} // Controls aktivieren oder deaktivieren
            />
            <ambientLight intensity={1} />
            <pointLight position={[0, 5, 5]} intensity={50} />
            <ParticleSource />
            <AnimatedSlits
              d={percentageD}
              w={percentageW}
              style={slitsSpring}
              isDoubleSlit={isDoubleSlit}
            />
            <Rail />
            <Scale scale={scale} distance={distance.toNumber("m")} />
            <InterferencePattern particles={particles} />
            {showCurve && (
              <IntensityCurve
                lambda={lambda}
                d={d}
                w={w}
                distance={distance}
                scale={scale}
                isDoubleSlit={isDoubleSlit}
                classic={classic}
              />
            )}
            {showBarChart && (
              <IntensityBarChart
                intensityMap={intensityMap}
                distance={distance.toNumber("m")}
                binSize={binSize}
              />
            )}
            <Screen distance={distance.toNumber("m")} />
          </Canvas>
        </Box>
        <ControlPanel
          w={w}
          setW={setW}
          d={d}
          setD={setD}
          setPercentageD={setPercentageD}
          setPercentageW={setPercentageW}
          isDoubleSlit={isDoubleSlit}
          setIsDoubleSlit={setIsDoubleSlit}
          classic={classic}
          setClassic={setClassic}
          lambda={lambda}
          setLambda={setLambda}
          scale={scale}
          setScale={setScale}
          handleFire={handleFire}
          handleFastFire={handleFastFire}
          isFiring={isFiring}
          maximumReached={maximumReached}
          handleClearParticles={handleClearParticles}
          showBarChart={showBarChart}
          setShowBarChart={setShowBarChart}
          showCurve={showCurve}
          setShowCurve={setShowCurve}
          setCameraPosition1={setCameraPosition1}
          setCameraPosition2={setCameraPosition2}
          shotParticleCount={particles}
        />
      </Box>
    </Box>
  );
}

export default App;
